import { MediaMatcher } from '@angular/cdk/layout';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription, firstValueFrom } from 'rxjs';
import {
  IBoton,
  ICategoria,
  ICliente,
  ICreateCategoria,
  IFilter,
  IListado,
  IQueryParam,
  IReemplazoCategoria,
  IUsoCategoriaPorCliente,
} from 'modelos/src';
import { DialogService } from '../../../auxiliares/dialog/dialog.service';
import { CategoriasService } from '../categorias.service';
import { HelperService } from '../../../auxiliares/servicios/helper.service';
import { ListadosService } from '../../../auxiliares/servicios/listados.service';
import { CrearEditarCategoriasComponent } from '../crear-editar-categorias/crear-editar-categorias.component';
import { IRegExpSearch } from '../../../auxiliares/filtro-tabla/filtro-tabla/filtro-tabla.component';
import {
  IDatosReemplazoDialog,
  IFilaReemplazo,
  ReemplazoCategoriaDialogComponent,
  ResultadoReemplazo,
} from '../reemplazo-categoria-dialog/reemplazo-categoria-dialog.component';

@Component({
  selector: 'app-listar-categorias',
  templateUrl: './listar-categorias.component.html',
  styleUrls: ['./listar-categorias.component.scss'],
  standalone: false,
})
export class ListarCategoriasComponent implements OnInit, OnDestroy {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private media = inject(MediaMatcher);
  private dialogService = inject(DialogService);
  private service = inject(CategoriasService);
  matDialog = inject(MatDialog);
  helper = inject(HelperService);
  private listadosService = inject(ListadosService);

  public search: IRegExpSearch = {
    fields: ['nombre', 'variante'],
  };

  public loading = true;
  public columnas = [
    'nombre',
    'nombreTemplate',
    'variante',
    'uso',
    'prioridad',
    'color',
    'twitter',
    'btnsPrincipales',
    'btnsSecundarios',
    'otrosBotones',
    'botonAlertaControl',
    'botonesRf',
    'acciones',
  ];
  public nombreColumnas = [
    'Nombre',
    'Nombre Template',
    'Variante',
    'Clientes',
    'Prioridad',
    'Color',
    'Twitter',
    'Btns Principales',
    'Btns Secundarios',
    'Otros Botones',
    'Control RF',
    'Botones RF',
    'Acciones',
  ];
  public saveColumnas = 'tabla-categorias';
  public dataSource = new MatTableDataSource<ICategoria>([]);
  // FILTRO BUSQUEDA Y PAGINACION
  private populate = [
    {
      path: 'btnsPrincipales',
      select: 'nombre variante',
    },
    {
      path: 'btnsSecundarios',
      select: 'nombre variante',
    },
    {
      path: 'otrosBotones',
      select: 'nombre variante',
    },
    {
      path: 'botonAlertaControl',
      select: 'nombre variante',
    },
  ];
  public queryParams: IQueryParam = {
    page: 0,
    limit: +this.helper.pageSize,
    sort: 'nombre',
    populate: JSON.stringify(this.populate),
  };
  public totalCount = 0;
  // Listado Continuo
  public listarContinuo = true;
  private datos$?: Subscription;
  // Mobile query
  public mobileQuery: MediaQueryList;
  //
  public botones: IBoton[] = [];
  private botones$?: Subscription;
  /** idCategoria -> nombres de los clientes que la tienen asignada. */
  public clientesPorCategoria: Record<string, string[]> = {};
  private clientes$?: Subscription;

  constructor() {
    this.mobileQuery = this.media.matchMedia('(max-width: 599px)');
    this.mobileQuery.addEventListener('change', () =>
      this.changeDetectorRef.detectChanges(),
    );
  }

  // ################################################################################
  // ################################# TABLA 1 ######################################
  // ################################################################################
  public pageEvent(event: PageEvent): void {
    this.helper.pageEvent(event);
    this.queryParams.page = event.pageIndex;
    this.queryParams.limit = event.pageSize;
    this.actualizar();
  }
  public sortChange(event: Sort): void {
    this.queryParams.sort =
      event.direction === 'asc' ? event.active : `-${event.active}`;
    this.actualizar();
  }
  public async cambioFiltro(filters: IFilter<ICategoria>) {
    this.loading = true;
    this.queryParams = {
      page: 0,
      limit: this.queryParams.limit,
      sort: this.queryParams.sort,
      populate: this.queryParams.populate,
      select: this.queryParams.select,
      filter: JSON.stringify(filters),
    };
    await this.actualizar();
    this.loading = false;
  }

  // ACCIONES
  public async crear(): Promise<void> {
    const config: MatDialogConfig = {
      width: '700px',
    };
    this.matDialog.open(CrearEditarCategoriasComponent, config);
  }

  public async editar(data: ICategoria) {
    const config: MatDialogConfig = {
      data,
      width: '700px',
    };
    this.matDialog.open(CrearEditarCategoriasComponent, config);
  }

  /**
   * Borra la categoría. Como las categorías son globales, antes hay que resolver a qué categoría
   * pasan los vecinos de CADA cliente que la esté usando: el reemplazo de uno no sirve para el
   * otro, porque tiene que ser una de las categorías que ese cliente tiene asignadas.
   */
  public async eliminar(dato: ICategoria): Promise<void> {
    try {
      this.loading = true;
      const uso = await firstValueFrom(this.service.uso(dato._id!));
      this.loading = false;

      // Sin uso no hay nada que reasignar: alcanza con confirmar.
      if (uso.sinUso) {
        const confirm = await this.dialogService.confirm(
          'Confirme la acción',
          `¿Eliminar la categoría ${dato.nombre}? No la usa ningún cliente.`,
        );
        if (!confirm) return;
        await this.ejecutarEliminacion(dato, []);
        return;
      }

      const filas: IFilaReemplazo[] = uso.clientes.map((cliente) => ({
        clave: cliente.idCliente,
        titulo: cliente.nombreCliente || cliente.idCliente,
        vecinosVigentes: cliente.vecinos.vigentes,
        opciones: cliente.reemplazosPosibles,
        nota: this.notaDeUso(cliente),
      }));

      const config: MatDialogConfig = {
        width: '650px',
        data: {
          titulo: `Eliminar "${dato.nombre}${dato.variante ? ' / ' + dato.variante : ''}"`,
          descripcion:
            uso.clientes.length > 1
              ? `La usan ${uso.clientes.length} clientes. Elegí a qué categoría pasan los vecinos de cada uno.`
              : 'Elegí a qué categoría pasan los vecinos que la tienen vigente.',
          filas,
          textoConfirmar: 'Eliminar',
        } as IDatosReemplazoDialog,
      };

      const resultado: ResultadoReemplazo | undefined = await firstValueFrom(
        this.matDialog
          .open(ReemplazoCategoriaDialogComponent, config)
          .afterClosed(),
      );
      if (!resultado) return;

      const reemplazos: IReemplazoCategoria[] = [...resultado].map(
        ([idCliente, idCategoriaReemplazo]) => ({
          idCliente,
          idCategoriaReemplazo,
        }),
      );
      await this.ejecutarEliminacion(dato, reemplazos);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
      this.loading = false;
    }
  }

  private async ejecutarEliminacion(
    dato: ICategoria,
    reemplazos: IReemplazoCategoria[],
  ): Promise<void> {
    try {
      this.loading = true;
      await firstValueFrom(
        this.service.eliminarConReemplazos(dato._id!, reemplazos),
      );
      this.helper.notifSuccess('Eliminación correcta');
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.loading = false;
    }
  }

  /** Qué más apunta a la categoría en ese cliente, además de los vecinos. */
  private notaDeUso(cliente: IUsoCategoriaPorCliente): string | undefined {
    const partes: string[] = [];
    if (cliente.esDefault) partes.push('es la categoría por defecto');
    if (cliente.rangosPorEdad) {
      partes.push(`${cliente.rangosPorEdad} rango(s) por edad`);
    }
    if (cliente.localidades) {
      partes.push(`${cliente.localidades} localidad(es) configurada(s)`);
    }
    if (cliente.vecinos.vencidos || cliente.vecinos.sinVencimiento) {
      partes.push(
        `${cliente.vecinos.vencidos + cliente.vecinos.sinVencimiento} vecino(s) con la categoría vencida o sin vencimiento (se limpian)`,
      );
    }
    if (!cliente.asignadaAlCliente) {
      partes.push('el cliente ya no la tiene asignada');
    }
    return partes.length ? partes.join(' · ') : undefined;
  }

  public async clonar(data: ICategoria) {
    const response = await this.dialogService.prompt(
      'Confirme la acción',
      `¿Clonar la categoría <strong>${data.nombre} ${
        data.variante ? data.variante : ''
      }</strong>?`,
      ['Nombre', 'Variante'],
      [data.nombre!, data.variante!],
    );
    const nombre = response ? response[0] : '';
    const variante = response ? response[1] : '';
    if (nombre) {
      const create: ICreateCategoria = {
        nombre,
        variante,
        nombreTemplate: data.nombreTemplate,
        prioridad: data.prioridad,
        color: data.color,
        editable: data.editable,
        idBotonAlertaControl: data.idBotonAlertaControl,
        idsBtnsPrincipales: data.idsBtnsPrincipales,
        idsBtnsSecundarios: data.idsBtnsSecundarios,
        idsOtrosBotones: data.idsOtrosBotones,
        tercerBoton: data.tercerBoton,
        twitter: data.twitter,
      };

      try {
        await firstValueFrom(this.service.crear(create));
        this.helper.notifSuccess(
          `Categoría clonada correctamente con el nombre ${nombre}`,
        );
      } catch (error) {
        console.error(error);
        this.helper.notifError(error);
      }
    }
  }

  // Listar
  public async actualizar(): Promise<void> {
    this.loading = true;
    await this.listar();
    this.loading = false;
  }

  private async listar(): Promise<void> {
    this.datos$?.unsubscribe();
    this.datos$ = this.listadosService
      .subscribe<IListado<ICategoria>>('categorias', this.queryParams)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.dataSource.data = data.datos;
        console.debug(
          new Date().toLocaleString(),
          `listado de categorias`,
          data,
        );
      });
    await this.listadosService.getLastValue('categorias', this.queryParams);
  }

  private async listarBotones(): Promise<void> {
    const query: IQueryParam = {
      select: 'nombre',
      limit: 0,
    };
    this.botones$?.unsubscribe();
    this.botones$ = this.listadosService
      .subscribe<IListado<IBoton>>('botones', query)
      .subscribe((data) => {
        this.botones = data.datos;
        console.debug(new Date().toLocaleString(), `listado de botones`, data);
      });
    await this.listadosService.getLastValue('botones', query);
  }

  public parseBotonRf(b: string): string {
    if (b === 'Reflector') return 'Reflector';
    if (b === 'Sirena') return 'Sirena';
    const boton = this.botones.find((btn) => btn._id === b);
    if (boton?.nombre) {
      return boton.nombre;
    } else {
      return 'Sin Configurar';
    }
  }

  /**
   * Clientes que tienen asignada cada categoría. Se calcula del listado de clientes y no se pide
   * por categoría: una columna que dispare un request por fila no vale lo que cuesta. El detalle
   * de vecinos, que sí necesita backend, aparece en el diálogo de borrado.
   */
  private async listarClientes(): Promise<void> {
    const query: IQueryParam = {
      select: 'nombre categorias',
      limit: 0,
    };
    this.clientes$?.unsubscribe();
    this.clientes$ = this.listadosService
      .subscribe<IListado<ICliente>>('clientes', query)
      .subscribe((data) => {
        const indice: Record<string, string[]> = {};
        for (const cliente of data.datos) {
          for (const cc of cliente.categorias ?? []) {
            if (!cc.idCategoria) continue;
            const id = String(cc.idCategoria);
            (indice[id] ??= []).push(cliente.nombre ?? '');
          }
        }
        this.clientesPorCategoria = indice;
      });
    await this.listadosService.getLastValue('clientes', query);
  }

  public cantidadClientes(idCategoria: string): number {
    return this.clientesPorCategoria[idCategoria]?.length ?? 0;
  }

  public clientesDe(idCategoria: string): string {
    const nombres = this.clientesPorCategoria[idCategoria] ?? [];
    return nombres.length ? nombres.join(', ') : 'Ningún cliente la usa';
  }

  //

  async ngOnInit(): Promise<void> {
    await Promise.all([
      this.actualizar(),
      this.listarBotones(),
      this.listarClientes(),
    ]);
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeEventListener('change', () =>
      this.changeDetectorRef.detectChanges(),
    );
    this.datos$?.unsubscribe();
    this.clientes$?.unsubscribe();
  }
}
